import { useCallback, useMemo } from "react";
import { Table, Row as TableRowType } from "@tanstack/react-table";
import { VirtualItem, Virtualizer } from "@tanstack/react-virtual";

import TableCell from "@/components/molecules/table/TableCell";
import { TableType } from "@/components/organisms/table/Table";
import { VirtualTablePaddings } from "@/components/organisms/table/table.utils";

export type TableRowProps<T extends { id: string }> = {
  table: Table<T>;
  row: TableRowType<T>;
  tableType: TableType;
  isLastRow: boolean;
  canReorderRows: boolean;
  onRemoveRows: (rowIds: string[]) => void;
  virtualItem: VirtualItem;
  hasContextMenu?: boolean;
  onRename?: (id: string, newValue: string) => void;
  canSelectRows?: boolean;
  virtualPaddings: VirtualTablePaddings;
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  draggedOverColumnId: string | null;
  onDetailClicked?: (rowId: string) => void;
  activeDetailId: string | null;
};

// TODO In the future, we should implement a context config to precisely define the context menu items
//  of each table type as not all tables have the same context menu items
const TableRow = <T extends { id: string }>({
  table,
  row,
  tableType = TableType.COMPANIES,
  isLastRow,
  canReorderRows = false,
  onRemoveRows,
  virtualItem,
  hasContextMenu = false,
  onRename,
  canSelectRows,
  virtualPaddings,
  columnVirtualizer,
  draggedOverColumnId,
  onDetailClicked,
  activeDetailId,
}: TableRowProps<T>) => {
  const visibleCells = row.getVisibleCells();
  if (visibleCells.length === 0) return <></>;
  const firstStickyColumn = visibleCells[0];

  const isDraggedOver = useCallback((cellId: string) => draggedOverColumnId === cellId, [draggedOverColumnId]);

  const canEdit = useMemo(() => onRename !== undefined, [onRename]);

  return (
    <>
      <tr
        style={{
          height: `${virtualItem.size}px`,
          transform: `translateY(${virtualItem.start}px)`,
        }}
        className={"group absolute flex w-full"}
      >
        <TableCell
          key={`${row.id}-${firstStickyColumn.id}`}
          table={table}
          cell={firstStickyColumn}
          index={0}
          row={row}
          isLastColumn={0 === row.getVisibleCells().length - 1}
          isLastRow={isLastRow}
          canReorderRows={canReorderRows}
          tableType={tableType}
          hasContextMenu={hasContextMenu}
          canEdit={canEdit}
          onRename={onRename}
          onRemoveRows={onRemoveRows}
          canSelectRows={canSelectRows}
          isDraggedOver={isDraggedOver(firstStickyColumn.id)}
          onDetailClicked={onDetailClicked}
          activeDetailId={activeDetailId}
        />
        {virtualPaddings.left ? <td style={{ display: "flex", width: virtualPaddings.left }} /> : null}
        {columnVirtualizer
          .getVirtualItems()
          .slice(1)
          .map((virtualColumn) => {
            const cell = visibleCells[virtualColumn.index];

            return (
              <TableCell
                key={`${row.id}-${cell.id}`}
                table={table}
                cell={cell}
                index={virtualColumn.index}
                row={row}
                isLastColumn={virtualColumn.index === row.getVisibleCells().length - 1}
                isLastRow={isLastRow}
                canReorderRows={canReorderRows}
                tableType={tableType}
                hasContextMenu={hasContextMenu}
                canEdit={false}
                onRename={onRename}
                onRemoveRows={onRemoveRows}
                canSelectRows={canSelectRows}
                isDraggedOver={isDraggedOver(cell.column.id)}
                onDetailClicked={onDetailClicked}
                activeDetailId={activeDetailId}
              />
            );
          })}
        {virtualPaddings.right ? <td style={{ display: "flex", width: virtualPaddings.right }} /> : null}
      </tr>
      {isLastRow && <div className={"h-2"} />}
    </>
  );
};

export default TableRow;
